import React, { useState, useEffect, useRef } from 'react';
import MapPanel from "../basic/MapPanel";
import MapLegend from '../specific/MapLegend';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import {
    buildColoredMapPathsPerWork,
    createMapValueLegends,
    getHistoryPointCoords,
    isPlantioStatusIniciado,
    parseMapNumericValue,
} from '../../usecases/mapPathUtils';

const shouldIncludePlantioItem = (item) => isPlantioStatusIniciado(item?.stat);

const getPlantioItems = (workData) => workData?.[2]?.histPlantio || [];

const buildSementesHaPaths = (data, legends) =>
    buildColoredMapPathsPerWork({
        data,
        getItems: getPlantioItems,
        legends,
        getValue: (item) => item.plp_sht,
        getCoords: getHistoryPointCoords,
        shouldIncludeItem: shouldIncludePlantioItem,
    });

const MapSementesHectare = ({ data, implementWidthMeters }) => {
    const [legends, setLegends] = useState([]);
    const [path, setPath] = useState([]);
    const [marker, setMarker] = useState([]);
    const [centerMap, setCenterMap] = useState(null);
    const [loadingMap, setLoadingMap] = useState(true);
    const [sliderValue, setSliderValue] = useState([0, 100]);
    const [realMin, setRealMin] = useState(0);
    const [realMax, setRealMax] = useState(100);
    const isInitialLoadRef = useRef(true);

    const createLegends = (newMin, newMax) => {
        const newLegends = createMapValueLegends(newMin, newMax);
        setLegends(newLegends);
        return newLegends;
    };
    
    
    const handleSliderChange = (event, newValue) => {
        setSliderValue(newValue);
    };

    // Processa o path segmentado por cores baseado nas legendas
    const processPath = () => {
        const currentHistory = data && data[2] && data[2].histPlantio ? data[2].histPlantio : [];
        
        if (!currentHistory || currentHistory.length === 0) {
            return;
        }

        if (!legends || legends.length === 0) {
            return; // Não limpa o path se não houver legendas
        }
        
        setPath(buildSementesHaPaths(data, legends));
    };

    const applyLimits = () => {
        const [newMin, newMax] = sliderValue;
        createLegends(newMin, newMax); // Recalcula as legendas; processPath será chamado via useEffect de legends
    };

    const handleLegendChange = (legendId) => {
        const legendsCopy = legends.map((legend) =>
            legend.id === legendId ? { ...legend, show: !legend.show } : legend
        );
        setLegends(legendsCopy);
        // processPath será chamado via useEffect([legends])
    };

    // Carregamento inicial: cria traçado segmentado colorido imediatamente
    useEffect(() => {
        isInitialLoadRef.current = true; // Reseta para carregamento inicial quando dados mudam
        const currentHistory = data && data[2] && data[2].histPlantio ? data[2].histPlantio : [];
        
        if (currentHistory && currentHistory.length > 0) {
            // Calcula min/max
            const min = currentHistory.reduce((acc, item) => {
                const value = parseMapNumericValue(item.plp_sht);
                return value != null && value > 0 && value < acc ? value : acc;
            }, Number.MAX_VALUE);
            const max = currentHistory.reduce((acc, item) => {
                const value = parseMapNumericValue(item.plp_sht) || 0;
                return value > acc ? value : acc;
            }, 0);
            const newRealMin = min !== Number.MAX_VALUE ? min : 0;
            const newRealMax = max;

            setRealMin(newRealMin);
            setRealMax(newRealMax);
            setSliderValue([newRealMin, newRealMax]);
            
            // Cria as legendas e obtém o retorno
            const newLegends = createLegends(newRealMin, newRealMax);

            // Marca que o carregamento inicial foi concluído
            isInitialLoadRef.current = false;

            // Processa o path segmentado colorido imediatamente (um path por trabalho)
            setPath(buildSementesHaPaths(data, newLegends));
            setLoadingMap(false);
    
            setMarker([
                { latitude: currentHistory[0].c_lat, longitude: currentHistory[0].c_lng, coordinateType: 'first' },
                { latitude: currentHistory[currentHistory.length - 1].c_lat, longitude: currentHistory[currentHistory.length - 1].c_lng, coordinateType: 'last' }
            ]);
            setCenterMap({ lat: currentHistory[0].c_lat, lng: currentHistory[0].c_lng });
        } else {
            setPath([]);
            setLoadingMap(false);
        }
    }, [data]);

    // Reprocessa caminho sempre que as legendas mudarem (após carregamento inicial)
    useEffect(() => {
        // Não processa durante o carregamento inicial - o traçado já foi definido
        if (isInitialLoadRef.current) {
            return;
        }
        if (legends && legends.length > 0) {
            processPath();
        }
    }, [legends]);
    


    return (
        <div style={{width: '100%', alignItems: 'center'}}>
            <h3>Variação de sementes por hectáre durante o trabalho</h3>
            {loadingMap ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '600px' }}>
                    <p>Carregando mapa...</p>
                </div>
            ) : (
                <>
                    <MapPanel markers={marker} zoomMap={17} centerMap={centerMap} linhas={path} height={600} implementWidthMeters={implementWidthMeters} />
                    <MapLegend legends={legends} onLegendChange={handleLegendChange} />
                    <div className="no-print" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '20px 0' }}>
                        <Slider
                            style={{ width: '60%', marginRight: '20px', marginLeft: '24px' }}
                            value={sliderValue}
                            onChange={handleSliderChange}
                            valueLabelDisplay="auto"
                            min={realMin}
                            max={realMax}
                        />
                        <Button 
                            onClick={applyLimits} 
                            style={{ backgroundColor: '#ffca20', color: '#000', borderRadius: '5px' }}
                        >
                            Aplicar Limites
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default MapSementesHectare;
